import { AttackPattern, PatternState } from '../AttackPattern.js';
import { createHostileFireAdapter } from './hostileFire.js';

/**
 * Understudy (Act III-2, ACT3-BIBLE §3) — the boss with no kit of its own.
 *
 * It instantiates the player's **actual weapon loadout** (the real `Weapon`
 * subclasses, at the player's current levels, fusions included) and fires them
 * back through the hostile-fire adapter, on a slower cadence and at a fraction
 * of the damage. Level a weapon up and its mirror levels with you.
 *
 * The near-miss this must clear: the Unmade King's `CorruptedCopy` already
 * reads the player's weapon — but only for a colour and an origin point, with a
 * fixed ring underneath. This runs the weapon code. It is also not Echo Duel,
 * which fights a *past run's* snapshot as a rival body in a separate mode; the
 * Understudy tracks THIS run, live.
 *
 * Mirrors are fresh instances (`new weapon.constructor()`), never the player's
 * own objects: every weapon carries stateful fire timers and pending-shot
 * queues, so borrowing the live instance would have the boss's volleys stealing
 * shots out of the player's own gun.
 *
 * Escalation: more slots mirrored per phase, and at `curtainCallPhase` the
 * Curtain Call — every mirrored weapon at once, at full player cadence, for a
 * short window.
 */
export class Understudy extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Understudy', telegraphDuration: 1.0, activeDuration: 5.2, minPhase });
    this.config = config;
    // Filled in by SpawningSystem, which injects the run's CombatSystem into
    // any pattern that declares the field (see its constructor).
    this.combat = null;
    this._mirrors = [];
    this._signature = null;
  }

  /** Slots mirrored right now — one more per phase, capped by the config. */
  _slotsFor(boss) {
    return Math.min(this.config.mirrorSlots, 1 + boss.phase);
  }

  /**
   * A cheap string identity for "which weapons, at which levels" — recomputing
   * mirrors every tick would throw away their fire timers, so they're rebuilt
   * only when this actually changes (which is what `refreshOnLevelUp` means).
   */
  _loadoutSignature(weapons) {
    return weapons.map((w) => `${w.name}@${w.level}`).join('|');
  }

  _rebuild(boss) {
    const weapons = this.combat?.weapons ?? [];
    const slots = weapons.slice(0, this._slotsFor(boss));
    this._mirrors = slots.map((weapon) => {
      // Every weapon class takes no constructor arguments (they read their own
      // entry out of config/constants.js), so this clones the *kind* of weapon
      // without cloning any of its live state.
      const mirror = new weapon.constructor();
      mirror.level = weapon.level;
      return mirror;
    });
    this._signature = this._loadoutSignature(weapons);
  }

  onBegin(boss) {
    this._elapsed = 0;
    this._curtainCall = 0;
    this._curtainCallCooldown = 2.2;
    this._rebuild(boss);
  }

  onActiveTick(dt, boss, player, pool) {
    this._elapsed += dt;
    const weapons = this.combat?.weapons ?? [];
    if (!weapons.length) return;
    if (this.config.refreshOnLevelUp !== false
      && (this._loadoutSignature(weapons) !== this._signature || this._mirrors.length !== this._slotsFor(boss))) {
      this._rebuild(boss);
    }

    // Curtain Call: for a couple of seconds the understudy stops holding back
    // and plays the part at the player's own tempo.
    let cadence = this.config.cadenceMultiplier;
    if (boss.phase >= (this.config.curtainCallPhase ?? 99)) {
      this._curtainCall -= dt;
      this._curtainCallCooldown -= dt;
      if (this._curtainCall <= 0 && this._curtainCallCooldown <= 0) {
        this._curtainCall = this.config.curtainCallDuration ?? 2.0;
        this._curtainCallCooldown = 5.0;
      }
      if (this._curtainCall > 0) cadence = 1;
    }

    const adapter = createHostileFireAdapter(pool, { damageScale: this.config.damageScale });
    // The origin is the boss; the target is the player. Weapons read `origin`
    // for position and for the player's own fire-rate multipliers (absent here,
    // which is correct — the boss doesn't inherit the player's buffs, only the
    // player's guns).
    const origin = { x: boss.x, y: boss.y };
    for (const mirror of this._mirrors) {
      // The slower cadence is a scaled dt, exactly the trick CombatSystem uses
      // to apply fire-rate multipliers to every weapon without any of them
      // knowing about it.
      mirror.update(dt * cadence, origin, player, adapter);
    }
  }

  getVisual(boss) {
    if (this.state !== PatternState.TELEGRAPH && this.state !== PatternState.ACTIVE) return null;
    return {
      kind: 'understudy',
      x: boss.x,
      y: boss.y,
      imminent: this.state === PatternState.ACTIVE,
      curtainCall: (this._curtainCall ?? 0) > 0,
      // Named on screen so the player can see their own build being read back,
      // which is the whole joke of the fight.
      weapons: this._mirrors.map((m) => m.name),
    };
  }
}
